import {getTestProps} from '@benchling/js-test-utils';
import {Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback} from 'react';

import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import PlusMenu from 'llm/chat-library/PlusMenu';
import {
  getAgentTypeText,
  getAgentDescriptionForChatInput,
  getAvailableAgentTypes,
  doesAgentTypeSupportRealtimeVoice,
  getDefaultAgentType,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import AIButton from 'llm/chat-library/ui/AIButton';
import {
  RealtimeVoiceSessionControls,
  RealtimeVoiceSessionState,
} from 'llm/realtime-voice/useRealtimeVoiceSession';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Spinner from 'ui-library/Spinner';
import isLikelyMobileCapabilities from 'util/isLikelyMobileCapabilities';

export function AgentToolbar({
  chatSessionId,
  textareaRef,
  fileInputRef,
  cameraInputRef,
  allowedChatTypes,
  agentType,
  setAgentType,
  inputDisabled,
  publicDataEnabled,
  setPublicDataEnabled,
  selectedMcpServerIds,
  setSelectedMcpServerIds,
  realtimeVoiceSessionControls,
  onSelectSkill,
}: {
  chatSessionId: string | null;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  allowedChatTypes: Array<ChatType>;
  agentType: AgentType | null;
  setAgentType: (agentType: AgentType | null) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (publicDataEnabled: boolean) => void;
  selectedMcpServerIds?: Array<string> | undefined;
  setSelectedMcpServerIds?: (serverIds: Array<string> | undefined) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  cameraInputRef: React.RefObject<HTMLInputElement>;
  inputDisabled: boolean;
  realtimeVoiceSessionControls: RealtimeVoiceSessionControls;
  onSelectSkill?: (skillName: string) => void;
}): JSX.Element {
  const handleCameraButtonClick = useCallback((): void => {
    if (!inputDisabled && cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  }, [inputDisabled, cameraInputRef]);

  return (
    <div
      {...getTestProps({component: 'AgentToolbar', element: 'root'})}
      className={css(styles.agentTypeButtons)}
      onMouseDown={(e) => {
        // Keep toolbar interactions from moving focus inside the chat input group.
        // This ensures :focus-within styles activate only when the textarea itself is focused.
        e.preventDefault();
      }}
    >
      <PlusMenu
        fileInputRef={fileInputRef}
        agentType={agentType}
        availableAgentTypes={getAvailableAgentTypes(allowedChatTypes)}
        setAgentType={setAgentType}
        inputDisabled={inputDisabled}
        publicDataEnabled={publicDataEnabled}
        setPublicDataEnabled={setPublicDataEnabled}
        selectedMcpServerIds={selectedMcpServerIds}
        setSelectedMcpServerIds={setSelectedMcpServerIds}
        onSelectSkill={onSelectSkill}
      />
      {/* Show a spinner while settings are loading (only possible for existing chats) */}
      {agentType == null ? (
        <Spinner aria-label="Loading agent settings" />
      ) : (
        (() => {
          const availableAgentTypes = getAvailableAgentTypes(allowedChatTypes);
          const defaultAgentType = getDefaultAgentType(null, allowedChatTypes);
          const isNonDefaultAgent = agentType !== defaultAgentType;
          const isAssistantEnabledForChat = availableAgentTypes.includes(AgentType.ASSISTANT);

          return availableAgentTypes.map((option: AgentType) => {
            // Assistant is implicitly enabled and never shown as a chip.
            if (option === AgentType.ASSISTANT) {
              return null;
            }

            // In Assistant-enabled chats, only render the currently selected non-Assistant chip.
            if (
              option !== agentType &&
              availableAgentTypes.length > 1 &&
              (isAssistantEnabledForChat || isNonDefaultAgent)
            ) {
              return null;
            }

            const showCloseIcon = isNonDefaultAgent && availableAgentTypes.length > 1 && option === agentType;
            const toggleButton = isAssistantEnabledForChat ? (
              <UnsetLegacyAgentButton
                key={option}
                agentType={option}
                onClick={() => {
                  setAgentType(defaultAgentType);
                }}
                disabled={inputDisabled}
                testKey={option}
              />
            ) : (
              <ToggleButton
                key={option}
                agentType={option}
                isActive={option === agentType}
                showCloseIcon={showCloseIcon}
                onClick={() => {
                  if (option === agentType) {
                    if (!showCloseIcon) {
                      return;
                    }
                    setAgentType(defaultAgentType);
                    return;
                  }
                  setAgentType(option);
                }}
                text={getAgentTypeText(option)}
                disabled={inputDisabled}
                testKey={option}
              />
            );
            return toggleButton;
          });
        })()
      )}
      {isLikelyMobileCapabilities() && (
        <AIButton
          ariaLabel="Take and upload photo"
          disabled={inputDisabled}
          icon="snapshot"
          onClick={handleCameraButtonClick}
        />
      )}
      {agentType != null &&
        doesAgentTypeSupportRealtimeVoice(agentType) &&
        chatSessionId != null &&
        (() => {
          switch (realtimeVoiceSessionControls.sessionState) {
            case RealtimeVoiceSessionState.NOT_CONNECTED:
              return (
                <AIButton
                  onClick={realtimeVoiceSessionControls.startVoiceSession}
                  disabled={inputDisabled}
                  icon="microphone"
                  text="Go live"
                />
              );
            case RealtimeVoiceSessionState.CONNECTING:
              return <AIButton disabled={true} text="Connecting..." />;
            case RealtimeVoiceSessionState.CONNECTED:
              return (
                <AIButton
                  onClick={realtimeVoiceSessionControls.stopVoiceSession}
                  disabled={inputDisabled}
                  icon="stop"
                  text="Stop"
                />
              );
            default:
              return null;
          }
        })()}
    </div>
  );
}

/**
 * A toggle button used to pick an agent or an agent feature like public data.
 */
function ToggleButton(props: {
  agentType: AgentType;
  isActive: boolean;
  text: React.ReactNode;
  showCloseIcon: boolean;
  onClick: () => void;
  disabled: boolean;
  testKey?: string;
}): JSX.Element {
  const {agentType, isActive, text, showCloseIcon, onClick, disabled, testKey} = props;
  const tooltipText = getAgentDescriptionForChatInput(agentType);
  const button = (
    // NB: I did not see an out of the box toggle button in Mantine v6.
    <Button
      aria-pressed={isActive}
      className={classNames({'is-active': isActive}, css(styles.agentTypeButton))}
      size={ButtonSize.MINI}
      buttonStyle={isActive ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_WHITE}
      onClick={onClick}
      textClassName={css(styles.agentTypeText)}
      text={
        <>
          {text}
          {showCloseIcon && <BenchlingIcon className={css(styles.closeIcon)} icon="close" />}
        </>
      }
      disabled={disabled}
      dataTestAttrs={testKey ? {component: 'ChatInput', element: 'agentToggle', key: testKey} : undefined}
    />
  );
  if (tooltipText == null) {
    return button;
  }
  return (
    <Tooltip label={tooltipText} position="top" openDelay={0} closeDelay={0} withinPortal={true}>
      <span>{button}</span>
    </Tooltip>
  );
}

function UnsetLegacyAgentButton(props: {
  agentType: AgentType;
  onClick: () => void;
  disabled: boolean;
  testKey?: string;
}): JSX.Element {
  const {agentType, onClick, disabled, testKey} = props;
  return (
    <AIButton
      ariaPressed={true}
      size="xs"
      onClick={onClick}
      isActive={true}
      text={getAgentTypeText(agentType)}
      disabled={disabled}
      dataTestAttrs={testKey ? {component: 'ChatInput', element: 'agentToggle', key: testKey} : undefined}
    />
  );
}

const styles = StyleSheet.create({
  agentTypeButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  agentTypeButton: {
    fontWeight: 400,
    fontSize: '13px',
    padding: '4px 10px 5px',
  },
  closeIcon: {
    marginLeft: '6px',
    marginRight: 0, // button has padding.
    opacity: 0.5,
  },
  agentTypeText: {
    flex: 'none',
  },
});
